import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_b2b-intelligence-2/artifacts/08rr2gpw_file_0000000021d08208bdb939d8b8dd4a57.png";

const PRIMARY_LINKS = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Membership", href: "/membership" },
  { label: "10XEXIM", href: "/members" },
  { label: "Resources", href: "/intelligence" },
  { label: "Events", href: "/events" },
  { label: "Contact", href: "/contact" },
];

const MORE_LINKS = [
  { label: "Opportunities", href: "/opportunities" },
  { label: "Industries", href: "/industries" },
  { label: "Capital Advisory", href: "/capital-advisory" },
  { label: "Post Requirement", href: "/rfq" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-navy-950/90 backdrop-blur-xl border-b border-slate-800" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
          <span className="logo-trail-wrap rounded-sm bg-white px-1.5 py-0.5">
            <img src={LOGO_URL} alt="10XGLOBAL — Connect | Collaborate | Grow" className="h-10 w-auto block" />
            <span className="logo-trail-glow" aria-hidden="true" />
            <span className="logo-trail-spark" aria-hidden="true" />
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {PRIMARY_LINKS.map((l) => (
            <Link key={l.href} to={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-[13px] font-medium text-slate-400 hover:text-gold transition-colors">{l.label}</Link>
          ))}
          <div className="relative">
            <button onClick={() => setMore(!more)} data-testid="nav-more" className="flex items-center gap-1 text-[13px] font-medium text-slate-400 hover:text-gold transition-colors">
              More <ChevronDown size={14} />
            </button>
            {more && (
              <div className="absolute right-0 top-8 w-52 card-surface p-2 shadow-2xl" onMouseLeave={() => setMore(false)}>
                {MORE_LINKS.map((l) => (
                  <Link key={l.href} to={l.href} onClick={() => setMore(false)} data-testid={`nav-more-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="block px-3 py-2 text-sm text-slate-300 hover:bg-navy-800 hover:text-gold rounded-sm">{l.label}</Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link to="/admin/login" data-testid="nav-admin" className="text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-gold transition-colors">Admin</Link>
          {user ? (
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-sign-out" className="btn-outline !px-4 !py-2 text-xs">SIGN OUT</button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-sign-in" className="text-[13px] font-semibold text-slate-300 hover:text-gold transition-colors">SIGN IN</Link>
              <Link to="/join" data-testid="nav-join" className="btn-gold !px-4 !py-2 text-xs">JOIN NOW</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-slate-300" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-800 bg-navy-950 px-4 py-4 space-y-1" data-testid="nav-mobile-menu">
          {[...PRIMARY_LINKS, ...MORE_LINKS].map((l) => (
            <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="block py-2 text-sm text-slate-300 hover:text-gold">{l.label}</Link>
          ))}
          <div className="flex gap-3 pt-3 border-t border-slate-800">
            <Link to="/admin/login" onClick={() => setOpen(false)} className="btn-outline text-xs" data-testid="nav-admin-mobile">ADMIN</Link>
            {user ? (
              <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="btn-outline flex-1 text-xs" data-testid="nav-sign-out-mobile">SIGN OUT</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline flex-1 text-xs">SIGN IN</Link>
                <Link to="/join" onClick={() => setOpen(false)} className="btn-gold flex-1 text-xs">JOIN NOW</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
